<template lang="pug">
.appointment-summary
  .summary-header
    h3 {{ headerTitle }}
    .date-time-pill
      span.pill {{ formatDate(new Date()) }}
      span.pill.time
        i.icon-clock
        |  {{ formatTime(new Date()) }}

  .summary-section
    div(v-for="(section, index) in flatSummary", :key="index")
      .section-header
        h4 {{ section.title }}

      template(v-if="section.pills?.length")
        span.pill(v-for="(pill, i) in section.pills", :key="i") {{ pill }}

      div.evolution-box(v-if="section.content?.length")
        div.content-item(v-for="(item, i) in section.content", :key="i")
          .content-item-header(v-if="!item.createdAt")
            button.remove-indication-btn(
              type="button"
              title="Quitar del resumen"
              @click="$emit('remove-indication', item.key)"
            ) ✕
          p(v-for="(line, j) in item.lines", :key="j")
            template(v-if="line.label?.toLowerCase() === 'medicamento' || line.label?.toLowerCase() === 'imágen' || line.label?.toLowerCase() === 'determinación'")
              template(v-if="Array.isArray(line.value)")
                strong.pill(v-for="(v, k) in line.value", :key="k") {{ v }}
              template(v-else)
                strong.pill {{ line.value }}
            template(v-else)
              strong {{ line.label }}:
              |  {{ line.value }}

      completed-forms-list(
        v-if="section.title?.toLowerCase().includes('formulario') && completedForms.length > 0"
        :completedForms="completedForms"
        :showHeader="false"
        :readonly="true"
        @edit-completed-form="onEditCompletedForm"
        @remove-completed-form="onRemoveCompletedForm"
      )
      
      completed-forms-list(
        v-if="section.programCompletedForms && section.programCompletedForms.length > 0"
        :completedForms="section.programCompletedForms"
        :showHeader="false"
        :readonly="true"
        @edit-completed-form="onEditCompletedForm"
        @remove-completed-form="onRemoveCompletedForm"
      )
</template>

<script>
import { format } from "date-fns";
import CompletedFormsList from "@/components/ui/CompletedFormsList.vue";

export default {
  name: "AppointmentSummary",
  components: {
    CompletedFormsList,
  },
  props: {
    savedIndications: {
      type: Array,
      default: () => [],
    },
    context: {
      type: String,
      default: "appointment",
      validator: (value) => ["appointment", "prescription"].includes(value),
    },
    completedForms: {
      type: Array,
      default: () => [],
    },
  },

  computed: {
    headerTitle() {
      const titles = {
        appointment: "Resumen de atención",
        prescription: "Resumen de prescripciones",
      };
      return titles[this.context] || titles.appointment;
    },
    flatSummary() {
      if (!this.savedIndications || !Array.isArray(this.savedIndications))
        return [];
      const sectionsMap = new Map();

      this.savedIndications.forEach((indication) => {
        let sectionTitle = indication.name;

        if (indication.type === "program") {
          if (indication.programData?.name) {
            sectionTitle = indication.programData.name;
          } else if (indication.defaultValues?.program?.value) {
            const programId = indication.defaultValues.program.value;
            const programName = this.getProgramName(programId);
            sectionTitle = programName || `Programa: ${programId}`;
          } else {
            sectionTitle = indication.name || "Programa";
          }
        }

        const values = indication.defaultValues || {};

        if (!sectionsMap.has(sectionTitle)) {
          sectionsMap.set(sectionTitle, []);
        }

        const lines = Object.entries(values)
          .filter(
            ([, val]) =>
              val.value !== undefined && val.value !== "" && val.value !== 0
          )
          .map(([key, val]) => ({
            label: val.friendlyName || key,
            value: this.getValueFormatted(val.value, val.inputType),
          }));

        if (lines.length) {
          sectionsMap.get(sectionTitle).push({
            key: indication.key,
            createdAt: indication.createdAt,
            lines,
          });
        }
      });

      return [...sectionsMap.entries()].map(([title, content]) => {
        const indication = this.savedIndications.find((ind) => {
          if (ind.type === "program") {
            return (
              ind.programData?.name === title ||
              this.getProgramName(ind.defaultValues?.program?.value) ===
                title ||
              ind.name === title
            );
          }
          return ind.name === title;
        });

        const programCompletedForms =
          indication?.type === "program" &&
          (indication.completedProgramForms || indication.initialData)
            ? indication.completedProgramForms || indication.initialData
            : null;

        return {
          title,
          content,
          programCompletedForms,
        };
      });
    },

    lockedAtStart() {
      const first = this.savedIndications?.find((item) => item.lockedAt);
      return first?.lockedAt || null;
    },
  },

  methods: {
    getValueFormatted(value, inputType) {
      if (!value) return "—";
      if (inputType === "diagnosisSelect") return this.getDiagnosisName(value);
      if (inputType === "medicationSelect")
        return this.getMedicationName(value);
      if (inputType === "imagesSelect") {
        const imageValues = this.getImageDescription(value);
        return Array.isArray(imageValues) ? imageValues : [imageValues];
      }
      if (inputType === "labSelect") {
        const labValues = this.getLabDescription(value);
        return Array.isArray(labValues) ? labValues : [labValues];
      }
      if (inputType === "programSelect") return this.getProgramName(value);
      if (inputType === "checkbox") return "Sí";
      return value;
    },

    getProgramName(id) {
      const program = this.$store.getters.getProgramById?.(id);
      if (program?.name) {
        return program.name;
      }
      return `Programa: ${id}`;
    },

    getMedicationName(id) {
      const response = this.$store.getters.getMedicationById(id);
      const name = Array.isArray(response) ? response[0]?.name : response?.name;
      return name || "Desconocido";
    },

    getDiagnosisName(id) {
      const diagnosis = this.$store.getters.getDiagnosisById(id);
      return diagnosis ? diagnosis.description : "Sin datos";
    },

    getImageDescription(value) {
      if (Array.isArray(value)) {
        return value.map((image) => this.getImageName(image));
      }

      return this.getImageName(value);
    },

    getImageName(imageValue) {
      const imageId =
        typeof imageValue === "object"
          ? imageValue?._id || imageValue?.id
          : imageValue;

      const imageFromCache = imageId
        ? this.$store.getters.getImageById(imageId)
        : null;

      const fallbackName =
        typeof imageValue === "object"
          ? imageValue?.description || imageValue?.name
          : null;

      return imageFromCache?.description || fallbackName || "Sin datos";
    },

    getLabDescription(codigo) {
      if (Array.isArray(codigo)) {
        return codigo.map((cod) => {
          const lab = this.$store.getters.getLabById(cod);
          return lab ? lab.description : "Sin datos";
        });
      }

      const lab = this.$store.getters.getLabById(codigo);
      return [lab ? lab.description : "Sin datos"];
    },

    formatDate(date) {
      return format(new Date(date), "dd/MM/yyyy");
    },
    formatTime(date) {
      return format(new Date(date), "HH:mm");
    },

    onEditCompletedForm(completedForm) {
      this.$emit("edit-completed-form", completedForm);
    },

    onRemoveCompletedForm(completedForm) {
      this.$emit("remove-completed-form", completedForm);
    },
  },
};
</script>

<style lang="scss" scoped>
.appointment-summary {
  background-color: $white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  font-size: 13px;
  color: #1a1a1a;
  display: flex;
  flex-direction: column;
  gap: 20px;
  font-family: "Segoe UI", sans-serif;
}

.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid #dbe2f0;
  padding-bottom: 8px;
  flex-wrap: wrap;
  gap: 10px;
}

.summary-header h3 {
  font-size: 13px;
  font-weight: bold;
  margin: 0;
}

.summary-header p {
  font-size: 13px;
  color: #666;
}

.summary-section {
  background-color: #fdfdfd;
  padding: 5px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: solid 2px $steel-blue;
  padding-bottom: 6px;
  margin-bottom: 10px;
  margin-top: 10px;
}

.section-header h4 {
  font-size: 13px;
  font-weight: 600;
}

.medication-box {
  margin-bottom: 9px;
  border-bottom: solid 2px rgba(0, 0, 0, 0.153);
}

.medication-box strong {
  font-weight: 600;
  color: $midnight-blue;
}

.medication-box p {
  color: $midnight-blue;
  margin: 2px 0;
  font-size: 13px;
}

.content-item {
  position: relative;
  margin-bottom: 9px;
  border-bottom: solid 2px rgba(0, 0, 0, 0.153);

  &:last-child {
    margin-bottom: 0;
    border-bottom: none;
  }
}

.content-item-header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 4px;
}

.remove-indication-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
  font-size: 12px;
  padding: 2px 4px;
  line-height: 1;
  border-radius: 4px;
  transition: color 0.15s, background 0.15s;

  &:hover {
    color: #e53e3e;
    background: #fee2e2;
  }
}

.evolution-box {
  margin-bottom: 9px;
  border-bottom: solid 2px rgba(0, 0, 0, 0.153);
}

.evolution-box p {
  font-size: 11px;
  padding-left: 1px;
  padding-right: 1px;
  margin: 2px 0;
  font-size: 13px;
  color: $midnight-blue;
}

.evolution-box strong {
  font-weight: 600;
  color: $midnight-blue;
}

.pill-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.date-time-pill {
  display: flex;
  gap: 6px;
}

.pill {
  background-color: #d5dbf366;
  color: $midnight-blue;
  padding: 4px 5px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  margin-top: 5px;
}

.pill.time i.icon-clock {
  margin-right: 4px;
  content: url('data:image/svg+xml;utf8,<svg fill="%2323498b" xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path d="M12 1a11 11 0 1 1 0 22A11 11 0 0 1 12 1Zm0 2a9 9 0 1 0 0 18A9 9 0 0 0 12 3Zm-.5 4h1v5.25l4.25 2.55-.5.85L11.5 13V7Z"/></svg>');
  display: inline-block;
  width: 13px;
  height: 13px;
}
</style>
